import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TalkJSService } from '../../core/services/talkjs/talkjs.service';
import Talk from 'talkjs';

declare var AppStorageUtil: any;

@Component({
  selector: 'app-talkjs-chat-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './talkjs-chat-popup.component.html',
  styleUrls: ['./talkjs-chat-popup.component.scss']
})
export class TalkjsChatPopupComponent implements OnInit, OnDestroy, OnChanges {
  // rfqId is optional so chat can also work without RFQ context
  @Input() rfqId?: number;
  @Input() otherUserId!: string;
  @Input() otherUserName!: string;
  @Input() otherUserEmail?: string;
  @Input() otherUserCompanyName?: string; // Company name for the other user
  @Input() rfqGuid?: string; // Encrypted/hashed RFQ ID for URL navigation
  /** Prefills the composer (TalkJS {@link ChatboxOptions.messageSuggestion}); buyer can edit before sending. */
  @Input() messageComposerSuggestion?: string;
  @Output() close = new EventEmitter<void>();

  showChat: boolean = false;
  showUpgradePrompt: boolean = false;
  isLoading: boolean = false;
  chatContainerId: string = '';
  private isInitialized: boolean = false;

  constructor(private talkjsService: TalkJSService) {
    this.chatContainerId = `talkjs-container-${Date.now()}`;
  }

  ngOnInit(): void {
    // Don't initialize here - wait for inputs to be set via ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Initialize when inputs are set or changed
    if ((changes["otherUserId"] || changes["rfqId"] || changes["messageComposerSuggestion"]) && !this.isInitialized) {
      // Only require otherUserId; rfqId is optional
      if (this.otherUserId) {
        this.checkAccessAndInitialize();
        this.isInitialized = true;
      }
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private checkAccessAndInitialize(): void {
    // Validate inputs before initializing
    if (!this.otherUserId) {
      console.warn('TalkJS Chat: Waiting for otherUserId to be set');
      return;
    }

    // Temporarily bypass account check for testing
    this.initializeChat();
    
    // TODO: Restore account type check once testing is complete
    // const accountType = AppStorageUtil.getItem('AccountType');
    // const isProfessional = AppStorageUtil.getItem('isProfessionalPackage') === 'true';
    // if (accountType === 'Gold' || accountType === 'Platinum' || isProfessional) {
    //   this.initializeChat();
    // } else {
    //   this.showUpgradePrompt = true;
    //   this.showChat = false;
    // }
  }

  private async initializeChat(): Promise<void> {
    // Validate required inputs before initializing
    if (!this.otherUserId || this.otherUserId.trim() === '') {
      console.error('TalkJS Chat: otherUserId is required but not provided');
      this.showChat = false;
      return;
    }

    if (!this.otherUserName || this.otherUserName.trim() === '') {
      console.warn('TalkJS Chat: otherUserName is not provided, using default');
      this.otherUserName = 'Contact';
    }

    this.showChat = true;
    this.isLoading = false; // Set to false first so the container renders

    try {
      // Create a unique, bidirectional conversationId for one-on-one chat
      // Sort user IDs to ensure same conversationId regardless of who initiates
      // This prevents group chat by ensuring each user pair has a unique conversation
      const currentUserId = AppStorageUtil.getItem('LoggedId');
      
      // Ensure otherUserId is a string (TalkJS requires string or number)
      const otherUserIdStr = String(this.otherUserId).trim();
      if (!otherUserIdStr) {
        console.error('TalkJS Chat: otherUserId is empty after conversion to string');
        this.showChat = false;
        return;
      }

      const userIds = [currentUserId, otherUserIdStr].sort();
      // If rfqId is present and valid (greater than 0), keep existing RFQ-specific conversation format.
      // Otherwise, fall back to a generic direct chat conversation id.
      // Note: rfqId of 0 is treated as invalid and will use generic chat format
      let conversationId: string;
      if (this.rfqId !== undefined && !isNaN(this.rfqId) && this.rfqId > 0) {
        conversationId = `rfq_${this.rfqId}_${userIds[0]}_${userIds[1]}`;
      } else {
        conversationId = `chat_${userIds[0]}_${userIds[1]}`;
      }
      
      // Wait for DOM to be ready after Angular renders the container
      setTimeout(async () => {
        try {
          const container = document.getElementById(this.chatContainerId);
          if (!container) {
            console.error('Chat container not found:', this.chatContainerId);
            return;
          }
          
          // Ensure rfqGuid is available (get from storage if not provided)
          const rfqGuidToUse = this.rfqGuid || AppStorageUtil.getItem('rfqGuid') || '';
          
          console.log('Mounting TalkJS to container:', this.chatContainerId);
          console.log('TalkJS Chat Inputs:', {
            rfqId: this.rfqId,
            rfqGuid: rfqGuidToUse,
            otherUserId: otherUserIdStr,
            otherUserName: this.otherUserName,
            currentUserId: currentUserId
          });
          
          await this.talkjsService.initializeChat(
            this.chatContainerId,
            conversationId,
            otherUserIdStr, // Use validated string version
            this.otherUserName,
            this.otherUserEmail,
            rfqGuidToUse, // Pass encrypted RFQ ID for URL navigation
            undefined, // userRole
            this.otherUserCompanyName,
            this.messageComposerSuggestion
          );
          console.log('TalkJS chat mounted successfully');
        } catch (error) {
          console.error('Error initializing TalkJS chat:', error);
          this.showChat = false;
        }
      }, 200); // Increased timeout to ensure DOM is ready

    } catch (error) {
      console.error('Error initializing chat:', error);
      this.showChat = false;
    }
  }

  onClose(): void {
    this.close.emit();
  }

  upgradeAccount(): void {
    // Navigate to packages page
    window.location.href = '/packages';
  }
}

